import type { Dish, Maybe, TransactionInventory } from "types";

export interface EffectiveRefillLedger {
	effectiveRemoved: Dish[];
	effectiveAdded: Dish[];
}

/**
 * Computes what one refill frame's inventory means for the Meal List, given the lists applied on
 * the previous frame (JID-2303). The BE lists are NET-cumulative vs the refill start and re-netted
 * non-monotonically (measured on live captures, 2026-07-29):
 *  - a dish added and then taken back DROPS OUT of `added` (it never permanently enters
 *    `removed`, which tracks original stock) — fold that dropout into the removals, or the dish
 *    stays as a phantom that keeps its emptied type on the Meal List;
 *  - dropouts from `removed` are deliberately IGNORED: every one observed was the BE re-netting
 *    an added-then-taken-back dish (it transits `removed` at STOCK_CHECK and drops out at
 *    FINISHED), and inferring a "restored original dish" re-add resurrected those as phantoms
 *    whenever the pass history was incomplete (kiosk reload mid-refill). A genuinely restored
 *    original dish — never observed — self-corrects via the first post-refill frame carrying its
 *    type. The effective lists never overlap, so applying removed-then-added stays deterministic.
 */
export const computeEffectiveRefillLedger = (
	prevInventory: Maybe<TransactionInventory> | undefined,
	inventory: Maybe<TransactionInventory> | undefined,
): EffectiveRefillLedger => {
	const removed = inventory?.removed ?? [];
	const added = inventory?.added ?? [];
	const addedIds = new Set(added.map((dish) => dish.id));
	const revokedAdds = (prevInventory?.added ?? []).filter((dish) => !addedIds.has(dish.id));
	return {
		effectiveRemoved: [...removed, ...revokedAdds],
		effectiveAdded: added,
	};
};
